import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Paper, Alert, Button } from '@mui/material';

/**
 * PublicReportViewer - View reports via QR code without authentication
 *
 * Route: /public/report/:reportType/:reportId/:token
 *
 * Fetches PDF from backend via fetch (uses proxy in dev), displays as blob.
 * Backend URL never exposed to client.
 *
 * IMPORTANT: This component must NOT require authentication.
 */
const PublicReportViewer = () => {
  const { reportType, reportId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  useEffect(() => {
    if (!reportType || !reportId || !token) {
      setError('Invalid report link. Missing required parameters.');
      setLoading(false);
      return;
    }

    const apiPath = `/api/public/report/${reportType}/${reportId}/${token}/pdf/`;

    let objectUrl = null;

    const fetchPdf = async () => {
      try {
        const response = await fetch(apiPath, {
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const pdfBlob = await response.blob();
        objectUrl = URL.createObjectURL(pdfBlob);

        setPdfUrl(objectUrl);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load PDF:', err);
        setError(err.message || 'Failed to load PDF. Invalid or expired link.');
        setLoading(false);
      }
    };

    fetchPdf();

    // Cleanup: revoke object URL when component unmounts
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [reportType, reportId, token]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
        p={3}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600 }}>
          <Alert severity="error">
            <Typography variant="h6" gutterBottom>
              Error Loading Report
            </Typography>
            <Typography>{error}</Typography>
          </Alert>
        </Paper>
      </Box>
    );
  }

  if (isMobile && pdfUrl) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
        gap={2}
      >
        <Typography variant="h6">Your report is ready.</Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => window.open(pdfUrl, '_blank')}
        >
          Open PDF
        </Button>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      bgcolor="#ffffff"
    >
      {pdfUrl ? (
        <iframe
          src={pdfUrl}
          title={`${reportType} Report ${reportId}`}
          style={{
            width: '100%',
            height: '100vh',
            border: 'none',
          }}
        />
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default PublicReportViewer;
